// src/components/common/LoadingSpinner.jsx - Componente de loading da Psiando Global

import React from 'react';
import logoPsiando from '@/assets/icon/logo_psiando.jpeg';

// Logo circular — mesmo padrão do Header/MainLayout
const LogoPSI = ({ size = "40", className = "" }) => {
  const dim = parseInt(size);
  return (
    <div className={className} style={{
      width: dim, height: dim,
      borderRadius: '50%',
      overflow: 'hidden',
      border: '3px solid var(--primary-accent)',
      boxShadow: '0 4px 16px rgba(26,92,88,0.25)',
      flexShrink: 0,
    }}>
      <img
        src={logoPsiando}
        alt="Psiando"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );
};

/**
 * Componente de spinner/loading reutilizável
 * @param {string} size - Tamanho do spinner (xs, sm, md, lg, xl)
 * @param {string} color - Cor do spinner (primary, secondary, white)
 * @param {string} className - Classes CSS adicionais
 * @param {string} text - Texto opcional para exibir abaixo do spinner
 * @param {boolean} overlay - Se deve mostrar overlay de fundo
 * @param {boolean} useLogo - Se deve usar o logo PSI ao invés do spinner padrão
 */
export const LoadingSpinner = ({ 
  size = 'md', 
  color = 'primary', 
  className = '', 
  text = '',
  overlay = false,
  useLogo = false
}) => {
  // Configurações de tamanho
  const sizes = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16'
  };

  // Configurações de tamanho para o logo PSI (valores em string para compatibilidade)
  const logoSizes = {
    xs: '24',
    sm: '32',
    md: '48',
    lg: '64',
    xl: '80'
  };

  // Configurações de cor
  const colors = {
    primary: 'border-[var(--primary-main)]',
    secondary: 'border-gray-500',
    white: 'border-white',
    success: 'border-green-500',
    warning: 'border-yellow-500',
    error: 'border-red-500'
  };

  // Classes base do spinner
  const spinnerClasses = `
    animate-spin 
    rounded-full 
    border-2 
    border-t-transparent 
    ${sizes[size]} 
    ${colors[color]} 
    ${className}
  `.trim().replace(/\s+/g, ' ');

  // Componente do spinner
  const Spinner = () => (
    <div className="flex flex-col items-center justify-center space-y-2">
      {useLogo ? (
        <div className="relative">
          <LogoPSI size={logoSizes[size]} />
          
          {/* Círculo rotativo ao redor do logo */}
          <div className="absolute inset-0 animate-spin" style={{ animationDuration: '2s' }}>
            <div className="absolute inset-1 border-2 border-transparent border-t-[var(--primary-accent)] border-r-[var(--secondary-light)] rounded-xl opacity-60"></div>
          </div>
        </div>
      ) : (
        <div className={spinnerClasses}></div>
      )}
      {text && (
        <p className="text-sm text-gray-600 animate-pulse">
          {text}
        </p>
      )}
    </div>
  );

  // Se overlay está habilitado, renderiza com fundo
  if (overlay) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 shadow-lg">
          <Spinner />
        </div>
      </div>
    );
  }

  // Renderização normal
  return <Spinner />;
};

/**
 * Variações pré-configuradas do LoadingSpinner
 */

// Spinner pequeno para botões
export const ButtonSpinner = ({ className = '' }) => (
  <LoadingSpinner 
    size="sm" 
    color="white" 
    className={className}
  />
);

// Spinner para páginas inteiras - agora usa o logo PSI simples
export const PageSpinner = ({ text = "Carregando..." }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <LoadingSpinner 
      size="lg" 
      color="primary" 
      text={text}
      useLogo={true}
    />
  </div>
);

// Spinner para cards/seções
export const SectionSpinner = ({ text = "Carregando dados..." }) => (
  <div className="flex items-center justify-center py-8">
    <LoadingSpinner 
      size="md" 
      color="primary" 
      text={text}
    />
  </div>
);

// Spinner com overlay para ações importantes - também usa o logo PSI simples
export const OverlaySpinner = ({ text = "Processando..." }) => (
  <LoadingSpinner 
    size="lg" 
    color="primary" 
    text={text}
    overlay={true}
    useLogo={true}
  />
);

// Logo PSI Spinner - nova variação específica para o logo
export const LogoSpinner = ({ size = "md", text = "Carregando...", className = "" }) => (
  <div className={`flex flex-col items-center justify-center space-y-3 ${className}`}>
    <div className="relative">
      <LogoPSI size={logoSizes[size]} />
      
      {/* Pontos orbitantes ao redor do logo */}
      <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s', animationDirection: 'reverse' }}>
        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full shadow-lg" style={{ backgroundColor: 'var(--primary-accent)' }}></div>
        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full shadow-lg" style={{ backgroundColor: 'var(--primary-main)' }}></div>
        <div className="absolute top-1/2 -left-1 transform -translate-y-1/2 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--secondary-light)' }}></div>
        <div className="absolute top-1/2 -right-1 transform -translate-y-1/2 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--primary-dark)' }}></div>
      </div>
    </div>
    
    {text && (
      <p className="text-sm text-gray-600 animate-pulse font-medium">
        {text}
      </p>
    )}
  </div>
);

/**
 * Hook personalizado para controle de loading
 */
export const useLoading = (initialState = false) => {
  const [loading, setLoading] = React.useState(initialState);

  const startLoading = () => setLoading(true);
  const stopLoading = () => setLoading(false);
  
  const withLoading = async (asyncFunction) => {
    try {
      startLoading();
      const result = await asyncFunction();
      return result;
    } finally {
      stopLoading();
    }
  };

  return {
    loading,
    startLoading,
    stopLoading,
    withLoading
  };
};

/**
 * Componente de skeleton loading para conteúdo específico
 */
export const SkeletonLoader = ({ 
  lines = 3, 
  className = '',
  showAvatar = false 
}) => (
  <div className={`animate-pulse ${className}`}>
    {showAvatar && (
      <div className="flex items-center space-x-4 mb-4">
        <div className="rounded-full bg-gray-300 h-10 w-10"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-300 rounded w-3/4"></div>
          <div className="h-3 bg-gray-300 rounded w-1/2"></div>
        </div>
      </div>
    )}
    
    <div className="space-y-3">
      {Array.from({ length: lines }).map((_, index) => (
        <div key={index} className="space-y-2">
          <div className="h-4 bg-gray-300 rounded"></div>
          <div className="h-4 bg-gray-300 rounded w-5/6"></div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Loading específico para tabelas
 */
export const TableSkeleton = ({ rows = 5, columns = 4 }) => (
  <div className="animate-pulse">
    <div className="bg-gray-100 h-12 rounded-t-lg mb-1"></div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="bg-white border-b border-gray-200 h-16 flex items-center px-4">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <div key={colIndex} className="flex-1 mx-2">
            <div className="h-4 bg-gray-300 rounded"></div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

/**
 * Loading para cards de agenda/agendamentos
 */
export const AgendaCardSkeleton = ({ count = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className="bg-white rounded-lg shadow-md p-4 animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="h-4 bg-gray-300 rounded w-1/4"></div>
          <div className="h-6 bg-gray-300 rounded-full w-16"></div>
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-300 rounded w-3/4"></div>
          <div className="h-3 bg-gray-300 rounded w-1/2"></div>
        </div>
        <div className="flex justify-between items-center mt-4">
          <div className="h-3 bg-gray-300 rounded w-1/3"></div>
          <div className="h-8 bg-gray-300 rounded w-20"></div>
        </div>
      </div>
    ))}
  </div>
);

export default LoadingSpinner;